import { useEffect, useState } from 'react';
import { BaseWizardProps } from '../wizard/wizard-step-base';
import { AppDispatch, RootState } from '../../state/store';
import { useDispatch, useSelector } from 'react-redux';
import { WizardButtonGroup } from '../wizard/wizard-button-group-component';
import {
  GoABlock,
  GoACallout,
  GoADropdown,
  GoADropdownItem,
  GoAFormItem,
  GoAInput,
  GoAInputDate,
  GoAInputText,
  GoASpacer,
} from '@abgov/react-components';
import {
  Collision,
  defaultVehicle,
  handleNestedChange,
  Vehicle,
} from '../../models/collision';
import {
  updateCollision,
  validateDriver,
} from '../../state/slices/collisionSlice';

import {
  pushNotification,
  removeAllValidationErrorNotifications,
} from '../../state/slices/notificationSlice';
import { NotificationType } from '../notifications/notification-component';

import styles from './driver-vehicle.module.css';
import { formatDateToISOWithTimezone } from '../../common/validation';
import { KeyValuePair } from '../../models/key-value-pair';
import useCsrService from '../../services/csr-api-redux';
import { formatPhoneNumber, formatPostalCode } from '../../utils/formUtils';
import {
  getLoginInfoFromKeycloakJwt,
  isTheAccountVerifiedByGoA,
  KeycloakJwtPayload,
} from '../../state/slices/authSlice';
import {
  getConfigurationByDescription,
  isHitAndRun,
  PositionInVehicle,
  SafetyEquipmentUsed,
} from '../../state/slices/configurationSlice';

interface DriverComponentProps extends BaseWizardProps {}

const defaultProvince = 'AB';
const useAppDispatch = () => useDispatch<AppDispatch>();
const useAppSelector: <TSelected>(
  selector: (state: RootState) => TSelected,
) => TSelected = useSelector;

export const DriverComponent = ({
  navigationConfiguration,
}: DriverComponentProps) => {
  const dispatch = useAppDispatch();
  const csrService = useCsrService(); // Move this into the component
  const stateCollision = useAppSelector((state: RootState) => state.collision);
  const stateAuth = useAppSelector((state: RootState) => state.auth);
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );
  const [errors, setErrors] = useState<
    {
      key: string;
      value: string;
    }[]
  >([]);

  const [vehicle, setVehicle] = useState<Vehicle>(
    stateCollision.collision.vehicles
      ? stateCollision.collision.vehicles[0]
      : defaultVehicle,
  );

  const [userInfoFromToken, setUserInfoFromToken] =
    useState<KeycloakJwtPayload | null>(() =>
      stateAuth.auth.access?.access
        ? getLoginInfoFromKeycloakJwt(stateAuth.auth.access.access)
        : null,
    );

  const unknownGenderId = '0';

  useEffect(() => {
    const pageConfig = navigationConfiguration.pagingConfiguration.find(
      (item) => item.default !== undefined,
    );

    setNextPage(pageConfig?.default ?? 'error');
  }, []);

  const [nextPage, setNextPage] = useState('');

  const validate = async (): Promise<boolean> => {
    let valid = true;
    const newErrors: {
      key: string;
      value: string;
    }[] = [];

    dispatch(removeAllValidationErrorNotifications());

    const driverLicenceRegex = /^\d{1,10}(-?\d{1,10})?$/;
    const phoneRegex = /^\(\d{3}\) \d{3}-\d{4}$/;
    const postalCodeRegex = /^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/;
    if (!vehicle.driver?.lastName) {
      newErrors.push({ key: 'lastName', value: 'Last name is required' });
      valid = false;
    }
    if (!vehicle.driver?.firstName) {
      newErrors.push({ key: 'firstName', value: 'First name is required' });
      valid = false;
    }

    if (!vehicle.driver?.dateOfBirth) {
      newErrors.push({
        key: 'dateOfBirth',
        value: 'Date of birth is required',
      });
      valid = false;
    }
    if (!vehicle.driver?.licenceNumber) {
      newErrors.push({
        key: 'licenceNumber',
        value: 'Drivers licence number is required',
      });
      valid = false;
    } else {
      if (!driverLicenceRegex.test(vehicle.driver?.licenceNumber)) {
        newErrors.push({
          key: 'licenceNumber',
          value: 'Drivers licence number is invalid',
        });
        valid = false;
      }
      const digitsOnly = vehicle.driver?.licenceNumber
        .replace(/[^0-9]/g, '')
        .replace(/^0+/, '');

      if (digitsOnly.length < 4) {
        newErrors.push({
          key: 'licenceNumber',
          value: 'Drivers licence number is invalid',
        });
        valid = false;
      }
    }

    if (!vehicle.driver?.address || !vehicle.driver?.address.address) {
      newErrors.push({ key: 'address', value: 'Address is required' });
      valid = false;
    }
    if (!vehicle.driver?.address || !vehicle.driver?.address.city) {
      newErrors.push({ key: 'city', value: 'City is required' });
      valid = false;
    }
    if (!vehicle.driver?.address || !vehicle.driver?.address.postalCode) {
      newErrors.push({ key: 'postalCode', value: 'Postal code is required' });
      valid = false;
    } else if (vehicle.driver?.address && vehicle.driver?.address.postalCode) {
      if (
        !postalCodeRegex.test(
          vehicle.driver?.address.postalCode.replace(' ', ''),
        )
      ) {
        newErrors.push({
          key: 'postalCode',
          value: 'Postal code is invalid, use (A1A 1A1 or A1A1A1) format',
        });
        valid = false;
      }
    }
    if (!vehicle.driver?.homePhoneNumber) {
      newErrors.push({
        key: 'homePhoneNumber',
        value: 'rimary phone number is required',
      });
      valid = false;
    } else {
      const validPhone = phoneRegex.test(vehicle.driver.homePhoneNumber);
      if (!validPhone) {
        newErrors.push({
          key: 'homePhoneNumber',
          value:
            'Primary phone number is invalid. Must be in the format (xxx) xxx-xxxx',
        });
        valid = false;
      }
    }

    if (vehicle.driver?.workPhoneNumber) {
      const validPhone = phoneRegex.test(vehicle.driver.workPhoneNumber);
      if (!validPhone) {
        newErrors.push({
          key: 'workPhoneNumber',
          value:
            'Work phone number is invalid. Must be in the format (xxx) xxx-xxxx',
        });
        valid = false;
      }
    }

    if (
      isHitAndRun(
        stateCollision.collision.collisionType!,
        stateConfiguration,
      ) === false
    ) {
      if (!vehicle.driver?.safetyEquipmentUsed) {
        newErrors.push({
          key: 'safetyEquipmentUsed',
          value: 'Safety equipment used is required',
        });
        valid = false;
      }
    }
    //finished doing client-side validation
    if (!handleValidationErrors(newErrors)) {
      return false; // Stop further validation if there are errors
    }

    //begin server-side validation against eCollision
    //default eCollision error message
    let eCollisionMessage =
      "The information below doesn't match our database. Please confirm it is correct and try again.";
    try {
      const result = await dispatch(
        validateDriver({ csrService, collision: updatedCollisionWithDriver }),
      ).unwrap();

      console.log(result);
      //validity is based on response from eCollision
      valid = result.payload.isValid;

      const newIsLicenceExpired = !!result.payload?.isExpired;
      const newIsLicenceSuspended = !!result.payload?.isSuspended;
      //Updating the state here as the handleNestedChange is fired late due to the async call
      if (
        updatedCollisionWithDriver.vehicles &&
        updatedCollisionWithDriver.vehicles[0].driver
      ) {
        updatedCollisionWithDriver.vehicles[0].driver.isLicenceExpired =
          newIsLicenceExpired;
        updatedCollisionWithDriver.vehicles[0].driver.isLicenceSuspended =
          newIsLicenceSuspended;
      }
    } catch (error) {
      console.clear();
      console.log(error);
      eCollisionMessage = 'An error occurred during driver validation.';
      valid = false;
    }

    if (!valid && !handleValidationErrors([], eCollisionMessage)) {
      return false; // Stop further validation if there are errors
    }

    return valid;
  };

  const handleValidationErrors = (
    errors: { key: string; value: string }[],
    bannerMessage?: string,
  ) => {
    let notificationMessage = 'Fill out the required information below.'; // default notification message

    // If there are errors, record them
    if (errors.length > 0) {
      setErrors(errors);
    } else {
      setErrors([]);
    }

    // Decide which message to display
    if (bannerMessage && bannerMessage.length > 0) {
      notificationMessage = bannerMessage;
    }

    // If no errors and no custom message, validation passes silently
    if (errors.length === 0 && (!bannerMessage || bannerMessage.length === 0)) {
      return true;
    }

    // If we have either errors or a custom message (or both), show notification and return false
    dispatch(
      pushNotification({
        body: notificationMessage,
        notificationType: NotificationType.importantValidationError,
      }),
    );

    return false;
  };

  //update the collision object without dispatching the save
  let updatedCollisionWithDriver: Collision = {
    ...stateCollision.collision,
    vehicles: [
      {
        ...stateCollision.collision.vehicles?.[0],
        ...vehicle,
        driver: {
          ...vehicle.driver!,
          positionInVehicleId: getConfigurationByDescription(
            stateConfiguration.configuration.positionInVehicle,
            PositionInVehicle.LEFT_FRONT,
          )?.id,

          firstName: vehicle.driver?.firstName
            ? vehicle.driver?.firstName
            : userInfoFromToken?.given_name
              ? userInfoFromToken?.given_name
              : '',

          lastName: vehicle.driver?.lastName
            ? vehicle.driver?.lastName
            : userInfoFromToken?.family_name
              ? userInfoFromToken?.family_name
              : '',

          gender: vehicle.driver?.gender
            ? vehicle.driver?.gender
            : unknownGenderId,

          dateOfBirth: vehicle.driver?.dateOfBirth
            ? formatDateToISOWithTimezone(
                new Date(vehicle.driver?.dateOfBirth! + 'T00:00:00'),
              )
            : userInfoFromToken?.birthdate
              ? formatDateToISOWithTimezone(
                  new Date(userInfoFromToken?.birthdate + 'T00:00:00'),
                )
              : undefined,

          safetyEquipmentUsed: isHitAndRun(
            stateCollision.collision.collisionType!,
            stateConfiguration,
          )
            ? getConfigurationByDescription(
                stateConfiguration.configuration.safetyEquipment,
                SafetyEquipmentUsed.NONE,
              )?.id
            : vehicle.driver?.safetyEquipmentUsed
              ? vehicle.driver?.safetyEquipmentUsed
              : undefined,

          licenceNumber: vehicle.driver?.licenceNumber
            ? vehicle.driver?.licenceNumber
            : userInfoFromToken?.ABDLNo
              ? userInfoFromToken?.ABDLNo
              : '',

          // default some stuff for mvp
          isLicenceValid: vehicle.driver?.isLicenceValid
            ? vehicle.driver?.isLicenceValid
            : true,
          licenceProvince: vehicle.driver?.licenceProvince
            ? vehicle.driver?.licenceProvince
            : defaultProvince,
          address: vehicle.driver?.address
            ? {
                ...vehicle.driver.address,
                province: defaultProvince,
                country: 'Canada',
              }
            : {
                address: '',
                city: '',
                postalCode: '',
                province: defaultProvince,
                country: 'Canada',
              },
        },
      },
    ],
  };

  const save = () => {
    let updatedCollision: Collision = updatedCollisionWithDriver;

    if (
      navigationConfiguration.updateMetadata &&
      navigationConfiguration.stepId
    ) {
      const stepUpdated = navigationConfiguration.updateMetadata(
        updatedCollision,
        navigationConfiguration.stepId!,
        navigationConfiguration.stageCompleted
          ? navigationConfiguration.stageCompleted
          : false,
        !navigationConfiguration.stageCompleted,
      );

      if (stepUpdated && updatedCollision.metadata) {
        updatedCollision.metadata = {
          ...updatedCollision.metadata,
          formState: stepUpdated,
        };
      }
    }

    dispatch(updateCollision(updatedCollision));

    if (navigationConfiguration.save) {
      navigationConfiguration.save();
    }
  };

  const handleCheckErrors = (fieldName: string): string => {
    const error = errors.find((x) => x.key == fieldName);

    if (error) {
      return error.value;
    }

    return '';
  };

  return (
    <div className={styles.container}>
      <div>
        <h1>Driver and vehicle information</h1>
        <p>Please provide the required information below.</p>
      </div>

      <div className={styles.formelements}>
        <PersonalInformationComponent
          collisionType={stateCollision.collision.collisionType!}
          firstNameFromToken={userInfoFromToken?.given_name}
          lastNameFromToken={userInfoFromToken?.family_name}
          dobFromToken={userInfoFromToken?.birthdate}
          licenceNumberFromToken={userInfoFromToken?.ABDLNo}
          vehicle={vehicle}
          onChange={handleNestedChange}
          stateUpdate={setVehicle}
          checkError={handleCheckErrors}
        />

        <GoASpacer vSpacing={'1'} />
        <GoASpacer vSpacing={'l'} />

        <ContactComponent
          vehicle={vehicle}
          onChange={handleNestedChange}
          stateUpdate={setVehicle}
          checkError={handleCheckErrors}
        />

        <GoASpacer vSpacing={'l'} />
        <GoASpacer vSpacing={'l'} />

        <div
          style={{
            display: isHitAndRun(
              stateCollision.collision.collisionType!,
              stateConfiguration,
            )
              ? 'none'
              : 'block',
          }}
        >
          <SafetyInformationComponent
            vehicle={vehicle}
            onChange={handleNestedChange}
            stateUpdate={setVehicle}
            checkError={handleCheckErrors}
          />
        </div>
        <WizardButtonGroup
          nextPage={nextPage}
          validate={validate}
          goToReview={navigationConfiguration.goToReview}
          save={save}
          navigate={navigationConfiguration.navigate}
        />
      </div>
    </div>
  );
};

interface PersonalInformationComponentProps {
  collisionType: number;
  firstNameFromToken?: string;
  lastNameFromToken?: string;
  dobFromToken?: string;
  licenceNumberFromToken?: string;
  genderFromToken?: string;
  vehicle: Vehicle;
  stateUpdate: any;
  onChange: any;
  checkError: (fieldName: string) => string;
}

export const PersonalInformationComponent = ({
  collisionType,
  firstNameFromToken,
  lastNameFromToken,
  dobFromToken,
  licenceNumberFromToken,
  vehicle,
  stateUpdate,
  onChange,
  checkError,
}: PersonalInformationComponentProps) => {
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  useEffect(() => {
    if (firstNameFromToken) {
      handleNestedChange(
        ['driver', 'firstName'],
        firstNameFromToken,
        stateUpdate,
      );
    }
    if (lastNameFromToken) {
      handleNestedChange(
        ['driver', 'lastName'],
        lastNameFromToken,
        stateUpdate,
      );
    }
    if (dobFromToken) {
      handleNestedChange(['driver', 'dateOfBirth'], dobFromToken, stateUpdate);
    }
    if (licenceNumberFromToken) {
      handleNestedChange(
        ['driver', 'licenceNumber'],
        licenceNumberFromToken,
        stateUpdate,
      );
    }
  }, [
    firstNameFromToken,
    lastNameFromToken,
    dobFromToken,
    licenceNumberFromToken,
  ]);

  return (
    <div>
      <div className="large-body">Personal information</div>
      <GoASpacer vSpacing={'m'} />
      <GoACallout type="information">
        The personal information fields have been pre-filled based on your
        verified Alberta.ca account. If any details appear incorrect, please
        update them through your Alberta.ca account
      </GoACallout>
      <GoAFormItem label="Last name" error={checkError('lastName')}>
        <GoAInput
          name="lastName"
          type={'text'}
          maxLength={30}
          value={vehicle.driver?.lastName || lastNameFromToken?.trim() || ''}
          disabled={true}
          width="100%"
          error={checkError('lastName').length > 0}
          onChange={(_, value) => {
            onChange(['driver', 'lastName'], value, stateUpdate);
          }}
        ></GoAInput>
      </GoAFormItem>
      <GoASpacer vSpacing={'m'} />

      <GoAFormItem label="First name" error={checkError('firstName')}>
        <GoAInputText
          name="firstName"
          maxLength={30}
          value={vehicle.driver?.firstName || firstNameFromToken?.trim() || ''}
          disabled={true}
          width="100%"
          error={checkError('firstName').length > 0}
          onChange={(_, value) => {
            onChange(['driver', 'firstName'], value, stateUpdate);
          }}
        ></GoAInputText>
      </GoAFormItem>

      <GoASpacer vSpacing={'m'} />

      <GoAFormItem label="Date of birth" error={checkError('dateOfBirth')}>
        <GoAInputDate
          name="dateOfBirth"
          value={
            dobFromToken ? new Date(dobFromToken + 'T00:00:00') : undefined
          }
          disabled={true}
          max={new Date()}
          width="100%"
          error={checkError('dateOfBirth').length > 0}
          onChange={(_, value) => {
            if (value) {
              const newDate = new Date(value);
              onChange(['driver', 'dateOfBirth'], newDate, stateUpdate);
            }
          }}
        ></GoAInputDate>
      </GoAFormItem>
      <GoASpacer vSpacing={'m'} />
      {isHitAndRun(collisionType, stateConfiguration) === false ? (
        <GoAFormItem
          label="Driver's licence"
          helpText="for example - 123456-123"
          error={checkError('licenceNumber')}
        >
          <GoAInputText
            name="licenceNumber"
            value={
              vehicle.driver?.licenceNumber || licenceNumberFromToken || ''
            }
            disabled={true}
            maxLength={25}
            width="100%"
            error={checkError('licenceNumber').length > 0}
            onChange={(_, value) => {
              onChange(['driver', 'licenceNumber'], value, stateUpdate);
            }}
          ></GoAInputText>
        </GoAFormItem>
      ) : (
        ''
      )}
    </div>
  );
};

interface SafetyInformationComponentProps {
  vehicle: Vehicle;
  stateUpdate: any;
  onChange: any;
  checkError: (fieldName: string) => string;
}

export const SafetyInformationComponent = ({
  vehicle,
  stateUpdate,
  onChange,
  checkError,
}: SafetyInformationComponentProps) => {
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  const filterSafetyEquipment = (
    safetyEquipment: KeyValuePair<number, string>[],
  ): KeyValuePair<number, string>[] => {
    return safetyEquipment.filter(
      (item) => item.value != SafetyEquipmentUsed.CHILD_SAFETY_BOOSTER_SEAT,
    );
  };

  return (
    <div>
      <div className="large-body">Safety information</div>
      <GoASpacer vSpacing={'m'} />

      <GoAFormItem
        label="Driver safety equipment used"
        error={checkError('safetyEquipmentUsed')}
        helpText="Select airbag option(s) only if deployed"
      >
        <GoADropdown
          value={vehicle.driver?.safetyEquipmentUsed?.toString() ?? ''}
          width="100%"
          placeholder="—Select—"
          error={checkError('safetyEquipmentUsed').length > 0}
          relative={true}
          onChange={(_, value) => {
            onChange(['driver', 'safetyEquipmentUsed'], value, stateUpdate);
          }}
        >
          {filterSafetyEquipment(
            stateConfiguration.configuration.safetyEquipment,
          ).map((x) => {
            return (
              <GoADropdownItem
                value={x.id.toString()}
                mountType="reset"
                label={x.formLabel}
                key={x.id}
              ></GoADropdownItem>
            );
          })}
        </GoADropdown>
      </GoAFormItem>
    </div>
  );
};

interface ContactComponentProps {
  vehicle: Vehicle;
  stateUpdate: any;
  onChange: any;
  checkError: (fieldName: string) => string;
}

const ContactComponent = ({
  vehicle,
  stateUpdate,
  onChange,
  checkError,
}: ContactComponentProps) => {
  const stateConfiguration = useAppSelector(
    (state: RootState) => state.configuration,
  );

  return (
    <div>
      <div className="large-body">Contact information</div>{' '}
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem label="Street address" error={checkError('address')}>
        <GoAInput
          name="address"
          type="text"
          maxLength={25}
          value={vehicle.driver?.address?.address ?? ''}
          width="100%"
          error={checkError('address').length > 0}
          onChange={(_, value) => {
            if (value) {
              onChange(['driver', 'address', 'address'], value, stateUpdate);
            }
          }}
        />
      </GoAFormItem>
      <GoASpacer vSpacing={'m'} />
      <GoAFormItem label="City or town" error={checkError('city')}>
        <GoAInput
          name="city"
          maxLength={25}
          type="text"
          value={vehicle.driver?.address?.city ?? ''}
          width="100%"
          error={checkError('city').length > 0}
          onChange={(_, value) => {
            if (value) {
              onChange(['driver', 'address', 'city'], value, stateUpdate);
            }
          }}
        />
      </GoAFormItem>
      <GoASpacer vSpacing={'m'} />
      <GoABlock direction="row" gap="l">
        <GoAFormItem label="Province or state" error={checkError('province')}>
          <GoADropdown
            name="province"
            disabled={true}
            value={
              stateConfiguration.configuration.provinceOrState &&
              stateConfiguration.configuration.provinceOrState.length > 0
                ? stateConfiguration.configuration.provinceOrState
                    .find((x) => x.id.toString() == defaultProvince)!
                    .id.toString()
                : ''
            }
            width="100%"
            error={checkError('province').length > 0}
            onChange={(_, value) => {
              if (value) {
                onChange(
                  ['driver', 'address', 'province'],
                  stateConfiguration.configuration.provinceOrState.find(
                    (x) => x.id == parseInt(value.toString()),
                  ),
                  stateUpdate,
                );
              }
            }}
          >
            {stateConfiguration.configuration.provinceOrState.map((x) => {
              return (
                <GoADropdownItem
                  value={x.id.toString()}
                  label={x.id.toString()}
                  key={x.id}
                ></GoADropdownItem>
              );
            })}
          </GoADropdown>
        </GoAFormItem>

        <GoAFormItem
          label="Postal or zip code"
          error={checkError('postalCode')}
        >
          <GoAInput
            name="postalCode"
            type="text"
            maxLength={10}
            value={vehicle.driver?.address?.postalCode ?? ''}
            width="100%"
            error={checkError('postalCode').length > 0}
            onChange={(_, value) => {
              if (value) {
                const formattedPostalCode = formatPostalCode(value, 'ca');
                onChange(
                  ['driver', 'address', 'postalCode'],
                  formattedPostalCode,
                  stateUpdate,
                );
              }
            }}
          />
        </GoAFormItem>
      </GoABlock>
      <GoASpacer vSpacing={'m'} />
      <GoABlock direction="row" gap="l">
        <GoAFormItem
          label="Primary phone"
          error={checkError('homePhoneNumber')}
          maxWidth="200px"
        >
          <GoAInput
            name="homePhoneNumber"
            type="tel"
            maxLength={14}
            placeholder="(xxx) xxx-xxxx"
            value={formatPhoneNumber(vehicle.driver?.homePhoneNumber ?? '')}
            width="100%"
            error={checkError('homePhoneNumber').length > 0}
            onChange={(_, value) => {
              if (value) {
                // Remove non-numeric characters for storage and reformat for display
                const formattedValue = formatPhoneNumber(value);
                onChange(
                  ['driver', 'homePhoneNumber'],
                  formattedValue,
                  stateUpdate,
                ); // Store cleaned value
              } else
                onChange(['driver', 'homePhoneNumber'], value, stateUpdate);
            }}
          />
        </GoAFormItem>
        <GoAFormItem
          label="Alternate phone"
          requirement="optional"
          error={checkError('workPhoneNumber')}
          maxWidth="200px"
        >
          <GoAInput
            name="workPhoneNumber"
            type="tel"
            maxLength={14}
            placeholder="(xxx) xxx-xxxx"
            value={formatPhoneNumber(vehicle.driver?.workPhoneNumber ?? '')}
            width="100%"
            error={checkError('workPhoneNumber').length > 0}
            onChange={(_, value) => {
              if (value) {
                // Remove non-numeric characters for storage and reformat for display
                const formattedValue = formatPhoneNumber(value);
                onChange(
                  ['driver', 'workPhoneNumber'],
                  formattedValue,
                  stateUpdate,
                ); // Store cleaned value
              } else
                onChange(['driver', 'workPhoneNumber'], value, stateUpdate);
            }}
          />
        </GoAFormItem>
      </GoABlock>
    </div>
  );
};
